// @flow
/* eslint react/no-danger:0 */

// Vendor libs
import React, { Component } from 'react';
import { connect } from 'react-redux';
import Button from 'app/common/react/components/growth/unauth/Button/Button';
import CONST from 'app/common/lib/Const';
import FbUtil from 'app/common/lib/FbUtil';
import GestaltTooltipWrapper from 'app/common/react/components/growth/unauth/signup/GestaltTooltipWrapper/GestaltTooltipWrapper';
import i18n from 'app/common/lib/i18n';
import login from 'app/common/react/components/growth/unauth/signup/UserLogin';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import PropTypes from 'prop-types';
import SocialConnect from 'app/common/react/components/growth/unauth/lib/SocialConnect';
import Tooltip from 'app/common/react/components/growth/unauth/signup/Tooltip/Tooltip';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import type { Viewer } from 'app/packages/pinterest-flow-types/viewer';
import UserRegistration from 'app/common/react/components/growth/unauth/signup/UserRegistration';
import { addUsersLoggedIn } from 'app/common/lib/MultipleAccountsUtils';
import { compose } from 'redux';
import { DefaultFontFamily } from 'app/common/react/components/common/FontFamily';
import { Icon } from 'gestalt';
import { isBirthdayOlderThanMinAge } from 'app/common/lib/react/dateUtils';
import { showError } from 'app/common/react/components/growth/unauth/lib/ErrorUtils';
import { SingleTreatmentExp } from 'app/common/lib/experiment';
import { track } from 'app/common/lib/react/analytics';
import { type SignupContext } from 'app/packages/pinterest-flow-types/registration';
import { withRouter } from 'react-router';
import trackRegisterAction, {
  trackRegisterActionWithClientData,
} from 'app/common/lib/util/trackRegisterAction';
import type {
  LoginCreds,
  FacebookUserData,
} from 'app/packages/pinterest-flow-types/authentication';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  checkFbPermission,
  FACEBOOK_SCOPE,
  trackLoginButtonDialogOpen,
} from 'app/packages/pinterest-authentication/facebook';
import {
  registrationRequiresAgeEN,
  registrationRequiresAgeGDPR,
  SIGNUP_METHOD,
} from 'app/packages/pinterest-authentication/registration';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { loadScriptWithWait, SCRIPT_TYPE } from 'app/packages/script-loader';

const styles = {
  button: {
    backgroundClip: 'padding-box',
    backgroundColor: '#4267b2',
    borderRadius: '2px',
    display: 'block',
    fontWeight: 'normal',
    height: '40px',
    marginTop: '0px',
    padding: '0px 0px 0px 8px',
    position: 'absolute',
    textAlign: 'left',
    transition: 'opacity .2s linear',
    width: '100%',
  },
  container: {
    position: 'relative',
  },
  tooltip: {
    top: '12px',
    left: '100%',
    position: 'absolute',
  },
  icon: {
    display: 'inline-block',
    margin: '8px',
  },
  text: {
    color: '#fff',
    display: 'inline-block',
    fontFamily: DefaultFontFamily,
    fontSize: '15px',
    fontWeight: 'bold',
    letterSpacing: 'normal',
    lineHeight: '16px',
    paddingLeft: '4px',
    paddingTop: '1px',
    textAlign: 'center',
    verticalAlign: 'text-bottom',
    WebkitFontSmoothing: 'auto', // 'auto' matches Facebook's iframe connect button 10/20/17
    whiteSpace: 'normal',
    width: '88%',
  },
  fbProfileButton: {
    height: '40px',
    textAlign: 'left',
  },
};

type OwnProps = {|
  buttonText: string,
  container?: string,
  disablePostSignupNextUrl?: boolean,
  isAccountSwitch?: boolean,
  isBusiness?: boolean,
  isRerequest?: boolean,
  inline?: boolean,
  loadingIndicatorCallback?: (boolean) => void,
  nextUrlParam?: string,
  onLoginWithMfa?: (creds: LoginCreds, Object) => void,
  onLoginWithSuspended?: () => void,
  preventRegister?: (string) => void,
  showFallback?: boolean,
  tooltip: boolean,
  useGestaltTooltip?: boolean,
  onSocialConnectVerified?: (
    network: string,
    userData: Object,
    credentialsAndLoggingData: Object,
  ) => void,
  onFBEmailDeniedPerm: () => void,
  type?: string,
  handlePostConnectClick?: (string) => void,
  handlePostSuccessConnect?: () => void,
  requestContext: RequestContextType,
  showLoginWelcomeLoadingFn?: (boolean) => void,
  showSignupWelcomeLoadingFn?: (boolean) => void,
  viewer: {
    isAuth: boolean,
    isLimitedLogin: boolean,
  },
  showResponseMessageModal?: (isSignup: boolean) => void,
|};

type StateProps = {|
  viewer: Viewer,
|};

type Props = {|
  ...OwnProps,
  ...StateProps,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

export const FACEBOOK_IDENTIFIER: 'facebook' = 'facebook';
const MIN_GDPR_EU_USER_AGE = 18;
const INTERVAL_IN_MILL_SECS = 100;

export class FacebookConnectButton extends Component<Props> {
  /**
   * Modeled off of LoginBase.js - connect()
   *
   * Get the facebook connect data
   */
  static contextTypes = {
    country: PropTypes.string,
    locale: PropTypes.string.isRequired,
    userAgent: PropTypes.shape({
      browserName: PropTypes.string.isRequired,
      isMobile: PropTypes.bool.isRequired,
      platform: PropTypes.string.isRequired,
      raw: PropTypes.string.isRequired,
    }).isRequired,
    hybridTier: PropTypes.string,
    pageType: PropTypes.string,
  };

  static defaultProps = {
    inline: false,
  };

  handleMessageEvent(event: MessageEvent) {
    trackLoginButtonDialogOpen(event, (action) => trackRegisterAction(`unauth_web.${action}`));
  }

  componentDidMount() {
    if (typeof window !== 'undefined') {
      window.statusChangeCallback = this.statusChangeCallback;
      window.checkLoginState = this.checkLoginState;
      if (window.fbConnectButtonContainerStack) {
        // Implementing a first in last out stack to track container props passed in <FacebookConnectButton />
        // Before the stack, when there are multiple <FacebookConnectButton/> instances on the page, only the container prop from the
        // first button mounted will be called regardless of which instance of the button is clicked.
        // Under the stack implementation, the container prop passed by the most recently mounted button would be logged
        window.fbConnectButtonContainerStack.push(this.props.container);
      } else {
        window.fbConnectButtonContainerStack = [this.props.container];
      }
      window.nextUrlParam = this.props.nextUrlParam;
      window.fbButtonMounted = window.fbButtonMounted ? window.fbButtonMounted + 1 : 1;
      window.addEventListener('message', this.handleMessageEvent, false);
    }

    const { userAgent } = this.context;
    if (userAgent.isMobile && userAgent.platform !== 'ios' && userAgent.platform !== 'ipad') {
      setTimeout(() => {
        if (window.__branchRedirectToStore || !window.__branchPinterestStarted) {
          this.initializeFB();
        } else {
          window.onbeforeunload = () => {
            if (window.__branchRedirectToStore) {
              this.initializeFB();
            }
          };
        }
      }, 50);
    } else if (
      window.fb_init !== PCONST.FacebookScriptLoadStatus.STARTED &&
      window.fb_init !== PCONST.FacebookScriptLoadStatus.FINISHED
    ) {
      // if there is no fb script load happenning then we load the script here

      this.initializeFB();
    } else if (window.fb_init === PCONST.FacebookScriptLoadStatus.FINISHED && window.FB.XFBML) {
      window.FB.XFBML.parse();
    } else {
      const intervalId = setInterval(() => {
        if (window.fb_init === PCONST.FacebookScriptLoadStatus.FINISHED && window.FB.XFBML) {
          window.FB.XFBML.parse();
          clearInterval(intervalId);
        }
      }, INTERVAL_IN_MILL_SECS);
    }
    if (this.props.isRerequest) {
      if (this.props.loadingIndicatorCallback) {
        this.props.loadingIndicatorCallback(false);
      }
      if (this.props.showSignupWelcomeLoadingFn) {
        this.props.showSignupWelcomeLoadingFn(false);
      }
    }
  }

  componentDidUpdate() {
    window.nextUrlParam = this.props.nextUrlParam;
  }

  componentWillUnmount() {
    if (typeof window !== 'undefined') {
      window.fbButtonMounted -= 1;
      if (window.fbConnectButtonContainerStack) {
        const idx = window.fbConnectButtonContainerStack.indexOf(this.props.container);
        if (idx >= 0) {
          window.fbConnectButtonContainerStack.splice(idx, 1);
        }
      }
      if (window.fbButtonMounted === 0) {
        delete window.statusChangeCallback;
        delete window.checkLoginState;
        delete window.nextUrlParam;
      }
      window.removeEventListener('message', this.handleMessageEvent);
    }
  }

  /**
   * Modeled off of LoginBase.js - onFacebookConnectSuccess()
   *
   * Try logging in to see if an account exists, then try signing up
   */
  onFacebookConnectSuccess(data: { id: string, access_token: string }) {
    const { preventRegister, container, isBusiness = false } = this.props;
    trackRegisterActionWithClientData('login.facebook_connect.success');
    const facebookInfo = {
      facebook_id: data.id,
      facebook_token: data.access_token,
    };
    const loggingData = {
      container,
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
      is_business: isBusiness,
    };
    login(facebookInfo, loggingData, this.props.seoUnauthExperiments).then(
      () => {
        trackRegisterActionWithClientData('login.facebook');
        this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_SUCCESS);
        this.navigateAfterLogin();
      },
      (error) => {
        this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_FAILURE);
        if (error.api_error_code === CONST.LOGIN_MFA_REQUIRED) {
          // TODO decide if we want to record success or not: https://jira.pinadmin.com/browse/SECGEN-2125
          // record successful facebook login
          trackRegisterActionWithClientData('login.facebook');
          // redirect to the LoginMfa screen
          if (this.props.onLoginWithMfa) {
            this.props.onLoginWithMfa(facebookInfo, error.data);
          }
        } else if (
          error.api_error_code === CONST.LOGIN_USER_NOT_FOUND ||
          error.api_error_code === CONST.FACEBOOK_ACCOUNT_NOT_LINKED
        ) {
          if (preventRegister) {
            preventRegister(FACEBOOK_IDENTIFIER);
            return;
          }
          this.registerWithFacebook(error, loggingData);
        } else {
          if (error && error.api_error_code) {
            trackRegisterAction(`facebook_button.failed_login_${error.api_error_code}`);
          }
          error.api_error_code === CONST.POLICY_VIOLATION_USER && this.props.onLoginWithSuspended
            ? this.props.onLoginWithSuspended()
            : showError(
                i18n._(
                  "Sorry, we can't log you in.",
                  'error message when user failed to login caused by suspend/deactivated etc.',
                ),
              );
        }
      },
    );
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_ATTEMPT);
  }

  getContainerName = () => {
    let containerName;
    if (
      window.fbConnectButtonContainerStack &&
      window.fbConnectButtonContainerStack[window.fbConnectButtonContainerStack.length - 1]
    ) {
      containerName =
        window.fbConnectButtonContainerStack[window.fbConnectButtonContainerStack.length - 1];
    }
    return containerName;
  };

  profilePicButton: ?HTMLElement;

  initializeFB() {
    if (this.profilePicButton !== null) {
      const inScriptLoaderExp = (
        this.props.seoUnauthExperiments.v2ActivateExperiment(
          'web_external_script_loader_with_wait',
        ) || ''
      ).startsWith('enabled');
      if (typeof window.FB !== 'undefined' && window.FB !== null) {
        window.FB.XFBML.parse();
      } else if (inScriptLoaderExp) {
        loadScriptWithWait(
          '//connect.facebook.net/' + FbUtil.getLocaleString(this.context.locale) + '/sdk.js',
          SCRIPT_TYPE.FACEBOOK,
          true,
        ).then(() => {
          window.FB.init({
            appId: CONST.GEN.templateConst.settings.FACEBOOK_API_KEY,
            status: true,
            xfbml: true,
            version: 'v2.7',
          });
        });
      } else {
        window.fbAsyncInit = function() {
          window.FB.init({
            appId: CONST.GEN.templateConst.settings.FACEBOOK_API_KEY,
            status: true,
            xfbml: true,
            version: 'v2.7',
          });
        };
        ((d, s, id) => {
          const fjs = d.getElementsByTagName(s)[0];
          if (d.getElementById(id)) {
            return;
          }
          const js = d.createElement(s);
          js.id = id;
          js.src =
            '//connect.facebook.net/' + FbUtil.getLocaleString(this.context.locale) + '/sdk.js';
          if (fjs.parentNode) {
            fjs.parentNode.insertBefore(js, fjs);
          }
        })(document, 'script', 'facebook-jssdk');
      }
    }
  }

  profilePicButtonConnectSuccess = (authResponse: { userID: string, accessToken: string }) => {
    const {
      container,
      handlePostSuccessConnect,
      isAccountSwitch,
      loadingIndicatorCallback,
      onLoginWithMfa,
      onLoginWithSuspended,
      preventRegister,
      requestContext,
      seoUnauthExperiments,
      isBusiness = false,
      showLoginWelcomeLoadingFn,
    } = this.props;
    if (loadingIndicatorCallback) {
      loadingIndicatorCallback(true);
    }
    if (showLoginWelcomeLoadingFn) {
      const inFBLoginWelcomeExp = new SingleTreatmentExp(
        requestContext,
        'web_login_welcome_fb',
      ).enabled();
      requestContext.experiments.flush();
      if (inFBLoginWelcomeExp) {
        showLoginWelcomeLoadingFn(true);
      }
    }
    const facebookInfo = {
      facebook_id: authResponse.userID,
      facebook_token: authResponse.accessToken,
    };
    const loggingData: SignupContext = {
      container: this.getContainerName(),
      hybridTier: this.context.hybridTier,
      page: this.context.pageType,
      is_business: isBusiness,
    };
    login(facebookInfo, loggingData, seoUnauthExperiments, isAccountSwitch).then(
      (response = {}) => {
        if (
          isAccountSwitch &&
          response.resource_response &&
          response.resource_response.data &&
          response.resource_response.data.user
        ) {
          const user = response.resource_response.data.user;
          addUsersLoggedIn(user, FACEBOOK_IDENTIFIER, container);
        }
        trackRegisterActionWithClientData('login.facebook');
        this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_SUCCESS);
        this.navigateAfterLogin();
        if (handlePostSuccessConnect) {
          handlePostSuccessConnect();
        }
      },
      (error) => {
        if (showLoginWelcomeLoadingFn) {
          showLoginWelcomeLoadingFn(false);
        }
        this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_FAILURE);
        if (error.api_error_code === CONST.LOGIN_MFA_REQUIRED) {
          if (loadingIndicatorCallback) {
            loadingIndicatorCallback(false);
          }
          // TODO decide if we want to record success or not: https://jira.pinadmin.com/browse/SECGEN-2125
          // record successful facebook login
          trackRegisterActionWithClientData('login.facebook');
          // redirect to the LoginMfa screen
          if (onLoginWithMfa) {
            onLoginWithMfa(facebookInfo, error.data);
          }
        } else if (
          error.api_error_code === CONST.LOGIN_USER_NOT_FOUND ||
          error.api_error_code === CONST.FACEBOOK_ACCOUNT_NOT_LINKED
        ) {
          if (preventRegister) {
            preventRegister(FACEBOOK_IDENTIFIER);
            return;
          }
          this.profilePicButtonRegisterWithFacebook(error, facebookInfo, loggingData);
        } else {
          if (error && error.api_error_code) {
            trackRegisterAction(`facebook_button.failed_login_${error.api_error_code}`);
          }
          if (
            error &&
            error.api_error_code === CONST.POLICY_VIOLATION_USER &&
            onLoginWithSuspended
          ) {
            onLoginWithSuspended();
          } else {
            showError(i18n._("Sorry, we can't log you in."));
          }
        }
      },
    );
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.LOGIN_ATTEMPT);
  };

  checkLoginState() {
    window.FB.getLoginStatus(function(response) {
      this.statusChangeCallback(response);
    });
  }

  statusChangeCallback = (response: {
    status?: string,
    authResponse: { userID: string, accessToken: string },
  }) => {
    let status = '';
    if (response.status === 'connected') {
      trackRegisterAction('web.facebook_connect_button.login_status.connected');
      this.profilePicButtonConnectSuccess(response.authResponse);
      status = 'success';
    } else if (response.status === 'not_authorized') {
      trackRegisterAction('web.facebook_connect_button.cancel.not_authorized');
      showError(i18n.commonStrings.connectError);
      status = 'not_authorized';
    } else {
      trackRegisterAction('web.facebook_connect_button.cancel.unknown');
      showError(i18n.commonStrings.connectError);
      status = 'other_error';
    }
    const { handlePostConnectClick } = this.props;
    if (handlePostConnectClick) {
      handlePostConnectClick(status);
    }
  };

  rerequestFacebookEmail: boolean;

  handleConnectClick = () => {
    trackRegisterAction('fallback_facebook_button.click');
    const socialConnect = new SocialConnect();
    trackRegisterActionWithClientData('login.facebook_connect.attempt');
    socialConnect.connect(
      SIGNUP_METHOD.FACEBOOK,
      {
        success: (connectData) => {
          this.onFacebookConnectSuccess(connectData);
        },
        error: (connectData) => {
          if (!connectData.alertedUpstream) {
            showError(i18n.commonStrings.connectError);
          }
        },
        rerequest_facebook_email: this.rerequestFacebookEmail,
      },
    );

    return false;
  };

  contextLogSignupLoginEvent(event: string) {
    const { viewer } = this.props;
    if (viewer.isAuth) {
      track(event, {
        login_state: viewer.isLimitedLogin
          ? PCONST.LoginStates.LIMITED
          : PCONST.LoginStates.DEFAULT,
        signup_login_method: PCONST.SignupLoginMethods.FACEBOOK,
      });
    } else {
      track(event, {
        signup_login_method: PCONST.SignupLoginMethods.FACEBOOK,
      });
    }
  }

  /**
   * Modeled off of LoginBase.js - registerWithFacebook()
   *
   */

  profilePicButtonRegisterWithFacebook = (
    error: {},
    facebookInfo: {},
    loggingData: SignupContext,
  ) => {
    const { country } = this.context;
    const {
      disablePostSignupNextUrl,
      loadingIndicatorCallback,
      nextUrlParam,
      onFBEmailDeniedPerm,
      onSocialConnectVerified,
      requestContext,
      showSignupWelcomeLoadingFn,
      showResponseMessageModal,
    } = this.props;

    const defaultErrorCallback = () => {
      if (loadingIndicatorCallback) {
        loadingIndicatorCallback(false);
      }
      if (error) {
        this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_FAILURE);
        showError(i18n._('Email required for registration.'));
      } else {
        this.navigate('/join/register/facebook/');
      }
    };

    const errorCallback = (rejectResult) => {
      if (rejectResult.socialConnectData && !rejectResult.socialConnectData.email) {
        checkFbPermission('email').then(({ hasPerm }) => {
          if (!hasPerm && onFBEmailDeniedPerm) {
            onFBEmailDeniedPerm();
          } else {
            defaultErrorCallback();
          }
        }, defaultErrorCallback);
      } else {
        defaultErrorCallback();
      }
    };

    const successCallback = (response) => {
      this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_SUCCESS);
      const { showResponseMessageModal } = this.props;
      if (showResponseMessageModal) {
        showResponseMessageModal(true);
      } else {
        loginUtil.handleSignupRedirect(nextUrlParam, disablePostSignupNextUrl);
      }
    };

    const shouldRequireAge =
      registrationRequiresAgeGDPR(country) || registrationRequiresAgeEN(country);

    if (onSocialConnectVerified && shouldRequireAge) {
      UserRegistration.verifyFacebookEmailExists().then((socialConnectData: FacebookUserData) => {
        // If we know user is >= 18 , directly register and don't show additional age step
        if (
          socialConnectData.birthday &&
          isBirthdayOlderThanMinAge(socialConnectData.birthday, MIN_GDPR_EU_USER_AGE)
        ) {
          // todo(sophia) We need to flush experiment triggering immediately (current experiment client default wraps
          // activate resource call in a queued resource call with 5 second delay). Longterm soln is to support immediate
          // flush in ExperimentChecker class.
          // https://paper.dropbox.com/doc/Web-Welcome-Loading-Screen-Experiments-Triggering-Issues--AMlvl51RMVYopimH6DjeJ8EzAg-BwroITTNabE6ckNqNRoqJ
          const inWelcomeLoadingExp = new SingleTreatmentExp(
            requestContext,
            'web_unauth_fb_welcome_v2',
          ).enabled();
          requestContext.experiments.flush();
          if (!showResponseMessageModal && showSignupWelcomeLoadingFn && inWelcomeLoadingExp) {
            showSignupWelcomeLoadingFn(true);
          }
          UserRegistration.attemptFacebookRegistration(
            Object.assign({}, facebookInfo, loggingData),
          ).then(successCallback, errorCallback);
          this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_ATTEMPT);
        } else {
          if (loadingIndicatorCallback) {
            loadingIndicatorCallback(false);
          }
          const credentialsAndLoggingData = Object.assign({}, facebookInfo, loggingData);
          onSocialConnectVerified(
            SIGNUP_METHOD.FACEBOOK,
            socialConnectData,
            credentialsAndLoggingData,
          );
        }
      }, errorCallback);
      return;
    }
    UserRegistration.attemptFacebookRegistration({
      ...facebookInfo,
      ...loggingData,
    }).then(successCallback, errorCallback);
  };

  /**
   * Modeled off of LoginBase.js - registerWithFacebook()
   *
   */
  navigate: (string) => void;

  registerWithFacebook = (error: {}, loggingData: SignupContext) => {
    const { country } = this.context;
    const { disablePostSignupNextUrl, nextUrlParam, onSocialConnectVerified } = this.props;
    const errorCallback = (rejectResult) => {
      if (this.props.loadingIndicatorCallback) {
        this.props.loadingIndicatorCallback(false);
      }
      if (error) {
        this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_FAILURE);
        this.rerequestFacebookEmail = true;
        showError(i18n._('Email required for registration.'));
      } else {
        this.navigate('/join/register/facebook/');
      }
    };

    const successCallback = () => {
      this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_SUCCESS);
      const { showResponseMessageModal } = this.props;
      if (showResponseMessageModal) {
        showResponseMessageModal(true);
      } else {
        loginUtil.handleSignupRedirect(
          nextUrlParam || window.location.pathname,
          disablePostSignupNextUrl,
        );
      }
    };

    if (onSocialConnectVerified && registrationRequiresAgeGDPR(country)) {
      UserRegistration.verifyFacebookEmailExists().then((socialConnectData: FacebookUserData) => {
        onSocialConnectVerified(SIGNUP_METHOD.FACEBOOK, socialConnectData, loggingData);
      }, errorCallback);
      return;
    }

    UserRegistration.attemptFacebookRegistration(loggingData).then(successCallback, errorCallback);
    this.contextLogSignupLoginEvent(PCONST.thrift.EventType.SIGNUP_ATTEMPT);
  };

  navigateAfterLogin = () => {
    const { showResponseMessageModal } = this.props;
    if (showResponseMessageModal) {
      showResponseMessageModal(true);
    } else {
      loginUtil.handleRedirect(this.props.nextUrlParam || window.location.pathname);
    }
  };

  /**
   * Modeled off of LoginBase.js - login()
   */
  login(creds: Object) {
    return login(creds, { container: 'fbconnectbtn' }, this.props.seoUnauthExperiments).then(() => {
      this.navigateAfterLogin();
    });
  }

  _fallbackButton: ?HTMLElement;

  renderTooltip = (tooltip: boolean) => {
    if (!tooltip) {
      return null;
    }

    if (this.props.useGestaltTooltip) {
      return <GestaltTooltipWrapper anchor={this._fallbackButton} message={tooltip} />;
    } else {
      return (
        <div style={styles.tooltip}>
          <Tooltip message={tooltip} />
        </div>
      );
    }
  };

  // This renders the facebook button with the profile picButton directly over it to include pf picture and name.
  // Because the better iframe profile pic button takes time to load, we first load old button so there is no blank
  // lag time.
  renderProfilePicButtonWithFallback() {
    const { showFallback } = this.props;

    /* Do not change data-scope without consulting activation and virality teams, and make sure they are ported over
    if this is rewritten */
    const { type, isRerequest } = this.props;
    const divContent =
      '<div class="fb-login-button" ' +
      `data-scope="${FACEBOOK_SCOPE.join(',')}" ` +
      'onlogin="checkLoginState"' +
      `data-button-type="${type === 'login' ? 'login_with' : 'continue_with'}" ` +
      `data-use-continue-as="${type === 'login' ? 'false' : 'true'}" ` +
      `data-width="${'268px'}"` +
      'data-size="large"></div>';
    const divContentWithRerequestFlag =
      '<div class="fb-login-button" ' +
      `data-scope="${FACEBOOK_SCOPE.join(',')}" ` +
      'onlogin="checkLoginState"' +
      `data-button-type="${type === 'login' ? 'login_with' : 'continue_with'}" ` +
      `data-use-continue-as="${type === 'login' ? 'false' : 'true'}" ` +
      `data-width="${'268px'}"` +
      'data-auth-type="rerequest"' +
      'data-size="large"></div>';
    const { buttonText, inline } = this.props;
    let buttonStyles = styles.button;
    if (inline) {
      buttonStyles = { ...buttonStyles, width: '48%', display: 'inline-block' };
    }
    if (showFallback) {
      buttonStyles = { ...buttonStyles, position: 'relative' };
    }
    const fbProfileButtonStyles = styles.fbProfileButton;
    // The <Button /> code is the fallback button.
    // The dangerouslySetInnerHTML code is the iframe that renders pf picture and name.
    return (
      <div
        ref={(ref) => {
          this._fallbackButton = ref;
        }}
      >
        <Button
          className="FacebookConnectButton"
          onClick={this.handleConnectClick}
          styleOverrides={buttonStyles}
          type="button"
        >
          <Icon
            accessibilityLabel={i18n._('facebook', 'accessible label for facebook icon')}
            color="white"
            icon="facebook"
            inline
            size={24}
          />
          <span style={styles.text}>{buttonText}</span>
        </Button>
        {!showFallback && (
          <div
            dangerouslySetInnerHTML={{
              __html: isRerequest ? divContentWithRerequestFlag : divContent,
            }}
            ref={(ref) => {
              this.profilePicButton = ref;
            }}
            style={fbProfileButtonStyles}
          />
        )}
      </div>
    );
  }

  render() {
    return (
      <div style={styles.container}>
        {this.renderProfilePicButtonWithFallback()}
        {this.renderTooltip(this.props.tooltip)}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  viewer: state.viewer,
});

export default compose(
  withRequestContext,
  connect(mapStateToProps),
  withUnauthSeoExperiments,
  withRouter,
)(FacebookConnectButton);
